/**
 * React Query hooks for the realtor's WhatsApp Business connection and the
 * review list of chats from numbers that are not contacts.
 */

import {
	type QueryClient,
	type UseQueryResult,
	useInfiniteQuery,
	useMutation,
	useQuery,
	useQueryClient,
} from "@tanstack/react-query";
import {
	type ConnectWhatsappInput,
	connectWhatsapp,
	disconnectWhatsapp,
	getWhatsappStatus,
	ignoreWhatsappReview,
	listWhatsappReview,
	saveWhatsappReview,
	type WhatsappConnection,
	type WhatsappReviewPage,
	type WhatsappReviewSaveItem,
	type WhatsappStatus,
	whatsappReviewFailure,
} from "@/features/realtor/services/whatsapp-api.service";
import { metaSignupConfig } from "@/features/realtor/utils/meta-sdk";
import { queryKeys } from "@/lib/query-keys";

/** How often the status refreshes while Meta is still sharing earlier chats. */
export const SYNC_REFRESH_MS = 15_000;

/**
 * `SYNC_REFRESH_MS` while the connection is connected and its sync is pending
 * or syncing, so the progress moves without a reload; otherwise false (no
 * refresh).
 */
export function statusRefreshInterval(
	status: WhatsappStatus | undefined,
): number | false {
	return isSyncing(status?.connection) ? SYNC_REFRESH_MS : false;
}

/** Connected, with Meta still sharing earlier chats. */
export function isSyncing(
	connection:
		| Pick<NonNullable<WhatsappStatus["connection"]>, "status" | "syncState">
		| null
		| undefined,
): boolean {
	return (
		connection?.status === "connected" &&
		(connection.syncState === "pending" || connection.syncState === "syncing")
	);
}

/**
 * The realtor's connection status, refreshed as `statusRefreshInterval`
 * says. With `enabled: false` nothing is fetched.
 */
export function useWhatsappStatus({
	enabled = true,
}: {
	enabled?: boolean;
} = {}): UseQueryResult<WhatsappStatus> {
	return useQuery({
		queryKey: queryKeys.realtor.whatsappConnection(),
		queryFn: getWhatsappStatus,
		enabled,
		refetchInterval: (query) => statusRefreshInterval(query.state.data),
	});
}

export interface WhatsappCapture {
	/** The status query, fetched only when `configured`. */
	status: UseQueryResult<WhatsappStatus>;
	/** This build carries the Meta config. */
	configured: boolean;
	/**
	 * Configured, with a loaded status saying the server has capture switched
	 * on: where the settings card shows.
	 */
	available: boolean;
	/** The realtor's connection while `available`, else null. */
	connection: WhatsappConnection | null;
}

/** The WhatsApp status as the surfaces that show capture read it. */
export function useWhatsappCapture(): WhatsappCapture {
	const configured = metaSignupConfig() !== null;
	const status = useWhatsappStatus({ enabled: configured });
	const available = configured && status.data?.available === true;
	return {
		status,
		configured,
		available,
		connection: available ? (status.data?.connection ?? null) : null,
	};
}

export function useConnectWhatsapp() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: (input: ConnectWhatsappInput) => connectWhatsapp(input),
		onSuccess: (status) => {
			queryClient.setQueryData(queryKeys.realtor.whatsappConnection(), status);
		},
	});
}

export function useDisconnectWhatsapp() {
	const queryClient = useQueryClient();
	return useMutation({
		mutationFn: () => disconnectWhatsapp(),
		onSuccess: (status) => {
			queryClient.setQueryData(queryKeys.realtor.whatsappConnection(), status);
		},
	});
}

// Review list -------------------------------------------------------------------

/**
 * The review list, newest first, a page at a time. While earlier chats are
 * still arriving it refreshes every `SYNC_REFRESH_MS`, every loaded page
 * included.
 */
export function useWhatsappReview() {
	return useInfiniteQuery({
		queryKey: queryKeys.realtor.whatsappReview(),
		queryFn: ({ pageParam }) => listWhatsappReview(pageParam),
		initialPageParam: undefined as string | undefined,
		getNextPageParam: (lastPage: WhatsappReviewPage) =>
			lastPage.nextCursor ?? undefined,
		refetchInterval: (query) =>
			isSyncing(query.state.data?.pages[0]?.connection)
				? SYNC_REFRESH_MS
				: false,
	});
}

/** Refreshes the review list and the status that carries its count. */
function invalidateReviewList(queryClient: QueryClient): Promise<unknown> {
	return Promise.all([
		queryClient.invalidateQueries({
			queryKey: queryKeys.realtor.whatsappReview(),
		}),
		queryClient.invalidateQueries({
			queryKey: queryKeys.realtor.whatsappConnection(),
		}),
	]);
}

/**
 * A refused save or ignore whose chats left the review list meanwhile: the
 * list and its count are refreshed so the page can show what remains.
 */
function useRefreshOnUnavailable() {
	const queryClient = useQueryClient();
	return (error: unknown) => {
		if (whatsappReviewFailure(error).code !== "REVIEW_ENTRIES_UNAVAILABLE") {
			return;
		}
		return invalidateReviewList(queryClient);
	};
}

/**
 * Saves chats as contacts, with a lead for each ticked one. It moves their
 * messages onto contacts, so besides the review list and its count, the
 * contacts book, the inbox, every timeline, the leads and the follow-ups go
 * stale, the same set as a chat import.
 */
export function useSaveWhatsappReview() {
	const queryClient = useQueryClient();
	const refreshOnUnavailable = useRefreshOnUnavailable();
	return useMutation({
		mutationFn: (entries: WhatsappReviewSaveItem[]) =>
			saveWhatsappReview(entries),
		onSuccess: () =>
			Promise.all([
				invalidateReviewList(queryClient),
				queryClient.invalidateQueries({
					queryKey: queryKeys.realtor.threads(),
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.realtor.contacts(),
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.realtor.timelines(),
				}),
				queryClient.invalidateQueries({
					queryKey: queryKeys.realtor.followUps(),
				}),
				queryClient.invalidateQueries({ queryKey: queryKeys.realtor.leads() }),
			]),
		onError: refreshOnUnavailable,
	});
}

/**
 * Ignores chats: deletes them and stops recording their numbers. Nothing
 * outside the review list holds them, so only the list and its count go
 * stale.
 */
export function useIgnoreWhatsappReview() {
	const queryClient = useQueryClient();
	const refreshOnUnavailable = useRefreshOnUnavailable();
	return useMutation({
		mutationFn: (entryIds: string[]) => ignoreWhatsappReview(entryIds),
		onSuccess: () => invalidateReviewList(queryClient),
		onError: refreshOnUnavailable,
	});
}
